import React, { useCallback, useEffect, useState } from 'react'
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip } from "chart.js";
import { HiX, HiPlus, HiTrash, HiCash, HiTrendingUp, HiCollection, HiExclamation } from 'react-icons/hi';

ChartJS.register(ArcElement, Tooltip);

// Fonction pour formater les nombres avec max 2 décimales
const formatNumber = (num: number): string => {
    return num.toFixed(2).replace(/\.00$/, '').replace(/(\.\d)0$/, '$1');
};

const encodeBudgetName = (name: string) => encodeURIComponent(name);

const Budget = () => {

    type BudgetProps = {
        name: string;
        amount: number;
        current: number;
    };

    const [budget, setBudget] = useState<BudgetProps[]>([
        {
            name: '',
            amount: 0,
            current: 0,
        },
    ]);
    const [popup, displayPopup] = useState<any>(undefined);
    const [selectedItem, setSelectedItem] = useState<any>(undefined);
    const [newItem, setNewItem] = useState<any>({
        name: '',
        amount: 0,
        date: new Date(),
    });

    useEffect(() => {
        fetch(`${process.env.REACT_APP_BACK_URL}/budget`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        })
        .then(async (response) => {
            if (response.ok) {
                setBudget(await response.json());
            }
        })
        .catch((error) => {
            console.error(error);
        });
    }, []);

    const getChartData = (item: BudgetProps) => {
        const isOverBudget = item.amount < 0;
        const totalBudget = item.amount + item.current;
        
        // Si dépassement, on affiche tout en rouge
        if (isOverBudget) {
            const data = [
                {
                    label: "Dépassement",
                    value: Math.abs(item.amount),
                    color: "rgba(239, 68, 68, 1)", // Rouge
                },
                {
                    label: "Budget initial",
                    value: totalBudget,
                    color: "rgba(229, 231, 235, 1)",
                },
            ];

            return {
                labels: data.map((d) => d.label),
                datasets: [
                    {
                        data: data.map((d) => Math.abs(d.value)),
                        backgroundColor: data.map((d) => d.color),
                        borderColor: ['transparent', 'transparent'],
                        borderWidth: 0,
                        borderRadius: 4,
                    },
                ],
            };
        }

        const data = [
            {
                label: "Disponible",
                value: item.amount,
                color: "rgba(99, 102, 241, 1)",
            },
            {
                label: "Dépensé",
                value: item.current,
                color: "rgba(229, 231, 235, 1)",
            },
        ];

        const finalData = {
            labels: data.map((d) => d.label),
            datasets: [
                {
                    data: data.map((d) => Math.round(d.value)),
                    backgroundColor: data.map((d) => d.color),
                    borderColor: ['transparent', 'transparent'],
                    borderWidth: 0,
                    borderRadius: 4,
                },
            ],
        };

        return finalData;
    }

    const options: any = {
        plugins: {
          datalabels: {
            formatter: function (value:any) {
              let val = Math.round(value);
              return new Intl.NumberFormat("fr-FR").format(val);
            },
            color: "white",
            font: {
              weight: 'bold',
              size:14,
              family: 'poppins',
            },
          },
          tooltip: {
            enabled: true,
            backgroundColor: 'rgba(15, 23, 42, 0.9)',
            padding: 12,
            cornerRadius: 8,
            titleFont: { size: 14, weight: 'bold' },
            bodyFont: { size: 13 },
          },
        },
        responsive: true,
        cutout: "70%",
        maintainAspectRatio: true,
    };

    const getItems = ((item: BudgetProps) => {
        fetch(`${process.env.REACT_APP_BACK_URL}/budget/${encodeBudgetName(item.name)}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        })
        .then(async (response) => {
            if (response.ok) {
                setSelectedItem(await response.json());
            }
        })
        .catch((error) => {
            console.error(error);
        });
    });

    const sendDatas = useCallback((name: string) => {
        fetch(`${process.env.REACT_APP_BACK_URL}/budget/${encodeBudgetName(name)}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(
                {
                    name: name,
                    content: newItem.name,
                    price: newItem.amount,
                    date: new Date(),
                }
            ),
        })
        .then(async (response) => {
            if (response.ok) {
                getItems(popup);
                setNewItem({ name: '', amount: 0, date: new Date() });
                fetch(`${process.env.REACT_APP_BACK_URL}/budget`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                .then(async (response) => {
                    if (response.ok) {
                        setBudget(await response.json());
                    }
                })
                .catch((error) => {
                    console.error(error);
                });
            }
        })
        .catch((error) => {
            console.error(error);
        });
    }, [newItem, popup]);


    const deleteTransaction = useCallback((id: number) => {
        if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette transaction ?')) return;
        
        fetch(`${process.env.REACT_APP_BACK_URL}/budget/${id}`, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
            },
        })
        .then(async (response) => {
            if (response.ok) {
                getItems(popup);
                fetch(`${process.env.REACT_APP_BACK_URL}/budget`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                .then(async (response) => {
                    if (response.ok) {
                        setBudget(await response.json());
                    }
                });
            }
        })
        .catch((error) => {
            console.error(error);
        });
    }, [popup]);

    // Calculate totals
    const totalBudget = budget.reduce((acc, item) => acc + item.amount + item.current, 0);
    const totalSpent = budget.reduce((acc, item) => acc + item.current, 0);
    const totalRemaining = budget.reduce((acc, item) => acc + item.amount, 0);

    return (
        <>
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                <h1 className="page-title">Gestion des Budgets</h1>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl p-5 text-white shadow-lg shadow-indigo-200">
                    <div className="flex items-center gap-3">
                        <div className="p-3 bg-white/20 rounded-xl">
                            <HiCash className="w-6 h-6" />
                        </div>
                        <div>
                            <p className="text-indigo-100 text-sm font-medium">Budget Total</p>
                            <p className="text-2xl font-bold">{formatNumber(totalBudget)} €</p>
                        </div>
                    </div>
                </div>
                <div className={`rounded-2xl p-5 text-white shadow-lg ${totalRemaining < 0 ? 'bg-gradient-to-br from-rose-500 to-red-600 shadow-rose-200' : 'bg-gradient-to-br from-emerald-500 to-teal-600 shadow-emerald-200'}`}>
                    <div className="flex items-center gap-3">
                        <div className="p-3 bg-white/20 rounded-xl">
                            {totalRemaining < 0 ? <HiExclamation className="w-6 h-6" /> : <HiTrendingUp className="w-6 h-6" />}
                        </div>
                        <div>
                            <p className={`text-sm font-medium ${totalRemaining < 0 ? 'text-rose-100' : 'text-emerald-100'}`}>{totalRemaining < 0 ? 'Dépassement' : 'Disponible'}</p>
                            <p className="text-2xl font-bold">{formatNumber(Math.abs(totalRemaining))} €</p>
                        </div>
                    </div>
                </div>
                <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl p-5 text-white shadow-lg shadow-amber-200">
                    <div className="flex items-center gap-3">
                        <div className="p-3 bg-white/20 rounded-xl">
                            <HiCollection className="w-6 h-6" />
                        </div>
                        <div>
                            <p className="text-amber-100 text-sm font-medium">Dépensé</p>
                            <p className="text-2xl font-bold">{formatNumber(totalSpent)} €</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Budget Cards Grid */}
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'>
                {
                    budget && budget.map((item, index) => {
                        const totalBudgetItem = item.amount + item.current;
                        const isOverBudget = item.amount < 0;
                        const percentage = isOverBudget 
                            ? Math.round((Math.abs(item.amount) / totalBudgetItem) * 100) 
                            : Math.round((item.amount / totalBudgetItem) * 100) || 0;
                        return (
                            <div 
                                key={index} 
                                className={`card p-6 cursor-pointer hover:shadow-xl transition-all duration-300 group ${isOverBudget ? 'ring-2 ring-rose-300' : ''}`}
                                onClick={() => {displayPopup(item); getItems(item)}}
                            >
                                <div className="relative w-32 h-32 mx-auto mb-4">
                                    <Doughnut data={getChartData(item)} options={options} />
                                    <div className="absolute inset-0 flex items-center justify-center flex-col">
                                        {isOverBudget ? (
                                            <>
                                                <HiExclamation className="w-5 h-5 text-rose-500" />
                                                <span className="text-lg font-bold text-rose-600">-{percentage}%</span>
                                            </>
                                        ) : (
                                            <span className="text-2xl font-bold text-indigo-600">{percentage}%</span>
                                        )}
                                    </div>
                                </div>
                                <div className="text-center">
                                    <h3 className="font-semibold text-lg text-slate-800 mb-1 group-hover:text-indigo-600 transition-colors">
                                        {item.name}
                                    </h3>
                                    {isOverBudget ? (
                                        <p className="text-sm text-rose-600 font-semibold">
                                            Dépassement de {formatNumber(Math.abs(item.amount))} €
                                        </p>
                                    ) : (
                                        <p className="text-sm text-slate-500">
                                            <span className="font-semibold text-indigo-600">{formatNumber(item.amount)} €</span> disponible
                                        </p>
                                    )}
                                    <p className="text-xs text-slate-400 mt-1">
                                        sur {formatNumber(totalBudgetItem)} € total
                                    </p>
                                </div>
                            </div>
                        );
                    })
                }
            </div>

            {/* Budget Detail Popup */}
            {popup !== undefined && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div 
                        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
                        onClick={() => displayPopup(undefined)}
                    ></div>
                    
                    <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col animate-scaleIn">
                        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-5 flex items-center justify-between rounded-t-2xl">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                                    <HiCash className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-white">{popup.name}</h3>
                                    <p className="text-indigo-200 text-sm">Historique des achats</p>
                                </div>
                            </div>
                            <button 
                                className="p-2 rounded-xl bg-white/10 hover:bg-white/20 transition-colors"
                                onClick={() => displayPopup(undefined)}
                            >
                                <HiX className="w-5 h-5 text-white" />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-6">
                            {selectedItem && selectedItem.length > 0 ? (
                                <div className="space-y-3">
                                    {selectedItem.map((item: any, index: number) => {
                                        const date = new Date(item.date);
                                        return (
                                            <div 
                                                key={index}
                                                className="flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors group"
                                            >
                                                <div className="flex items-center gap-4">
                                                    <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                                                        <HiCollection className="w-5 h-5 text-indigo-600" />
                                                    </div>
                                                    <div>
                                                        <p className="font-medium text-slate-800">{item.content}</p>
                                                        <p className="text-sm text-slate-500">
                                                            {date.toLocaleDateString('fr-FR', { 
                                                                day: 'numeric', 
                                                                month: 'long', 
                                                                year: 'numeric',
                                                                hour: '2-digit',
                                                                minute: '2-digit'
                                                            })}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <span className="text-lg font-bold text-rose-600">-{formatNumber(Number(item.price))} €</span>
                                                    <button 
                                                        onClick={() => deleteTransaction(item.id)} 
                                                        className="p-2 rounded-lg bg-slate-200 text-slate-400 hover:bg-rose-100 hover:text-rose-600 transition-colors opacity-0 group-hover:opacity-100"
                                                    >
                                                        <HiTrash className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="text-center py-8">
                                    <div className="w-16 h-16 mx-auto mb-4 bg-slate-100 rounded-full flex items-center justify-center">
                                        <HiCollection className="w-8 h-8 text-slate-400" />
                                    </div>
                                    <p className="text-slate-500">Aucun achat enregistré</p>
                                </div>
                            )}
                        </div>

                        <div className="bg-slate-50 px-6 py-4 border-t border-slate-200 rounded-b-2xl">
                            <p className="text-sm font-medium text-slate-600 mb-3">Ajouter un achat</p>
                            <div className="flex flex-col sm:flex-row gap-3">
                                <input
                                    type="text"
                                    placeholder="Titre de l'achat"
                                    className="input-modern flex-[3]"
                                    value={newItem.name}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                        setNewItem((prev: typeof newItem) => ({ ...prev, name: e.target.value }));
                                    }}
                                />
                                <div className="flex gap-3">
                                    <div className="relative">
                                        <input
                                            type="number"
                                            placeholder="0.00"
                                            className="input-modern w-28 pr-8"
                                            value={newItem.amount || ''}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                setNewItem((prev: typeof newItem) => ({ ...prev, amount: Number(e.target.value) }));
                                            }}
                                        />
                                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 font-medium">€</span>
                                    </div>
                                    <button 
                                        onClick={() => sendDatas(popup.name)}
                                        className="btn-primary flex items-center gap-2 whitespace-nowrap"
                                        disabled={!newItem.name || !newItem.amount}
                                    >
                                        <HiPlus className="w-5 h-5" />
                                        <span className="hidden sm:inline">Ajouter</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default Budget